import { useEffect, useState } from "react";
import {
  acceptInvite,
  ApiError,
  confirmPasswordReset,
  fetchFeaturedSignal,
  fetchPublicFlows,
  fetchPublicScorecard,
  fetchUniverseStats,
  login,
  requestInvite,
  type PublicFlowsDto,
  type PublicSignalDto,
  type ScorecardDto,
  type UniverseStatsDto,
} from "../api";
import { FlowsChart } from "../components/FlowsChart";
import "../landing.css";

/**
 * Public landing page (route "/", pre-login).
 *
 * Group B (2026-09-06, +2 endpoints added 2026-09-22): the teasers show REAL
 * data from deliberate public (no-auth) endpoints -- there is no token to
 * send pre-login, so these are the only unauthenticated data routes in the
 * whole API:
 *   1. Flows      -> GET /api/market/flows              (fii_dii_flows -- genuinely thin, ~1 row)
 *   2. Signal     -> GET /api/instrument/featured/signal (the real, currently best-covered,
 *                    highest-signal large/mid/small-cap stock -- picked server-side, cached
 *                    for the day, never cap:micro; NOT a hardcoded ticker anymore)
 *   3. Scorecard  -> GET /api/models/scorecard           (training_run backtest vs model_performance live)
 *   4. Hero stats -> GET /api/market/universe-stats      (real instrument/bar counts, not hardcoded text)
 *
 * THE RULE THAT SURVIVED FROM THE PLACEHOLDER PHASE: a public teaser does
 * not get to drop the app's honesty caveats just because it is marketing.
 * Sigma stays rank-space (never shown as "%"), gaps stay surfaced, small-n
 * stays flagged, and the flows teaser does NOT draw a chart it doesn't have
 * the data for -- fii_dii_flows has ~1 row, so the fake "12 sessions"
 * sparkline from the placeholder phase is retired in favour of one honest
 * reading, labelled exactly what it is.
 */

function LiveBadge() {
  return <span className="l-badge-live">● Live data</span>;
}

function BrowserBar({ url, badge }: { url: string; badge?: React.ReactNode }) {
  return (
    <div className="l-bbar">
      <span className="l-bdot" style={{ background: "var(--bad)" }} />
      <span className="l-bdot" style={{ background: "var(--warn)" }} />
      <span className="l-bdot" style={{ background: "var(--ok)" }} />
      <span className="l-burl">{url}</span>
      {badge ?? <LiveBadge />}
    </div>
  );
}

function fmtIC(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(4)}`;
}

function fmtCrore(v: number | null): string {
  if (v === null) return "—";
  return `${v >= 0 ? "+" : ""}${v.toLocaleString("en-IN")}`;
}

export function LandingPage({ onLoggedIn }: { onLoggedIn: (token: string, role: string, userId: string) => void }) {
  const [tab, setTab] = useState<"invite" | "login">("invite");
  const [toast, setToast] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [useCase, setUseCase] = useState("Individual investor — research");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginBusy, setLoginBusy] = useState(false);

  const [flows, setFlows] = useState<PublicFlowsDto | null>(null);
  const [flowsError, setFlowsError] = useState(false);
  const [signal, setSignal] = useState<PublicSignalDto | null>(null);
  const [signalError, setSignalError] = useState(false);
  const [scorecard, setScorecard] = useState<ScorecardDto | null>(null);
  const [scorecardError, setScorecardError] = useState(false);
  const [stats, setStats] = useState<UniverseStatsDto | null>(null);
  const [statsError, setStatsError] = useState(false);

  const [inviteBusy, setInviteBusy] = useState(false);

  // A ?invite_token= or ?reset_token= in the URL (from an emailed link)
  // means this load is a token-consumption visit, not a normal landing view
  // -- captured once on mount since the query string doesn't change again.
  const [pendingToken] = useState<{ kind: "invite" | "reset"; token: string } | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get("invite_token");
    const resetToken = params.get("reset_token");
    if (inviteToken) return { kind: "invite", token: inviteToken };
    if (resetToken) return { kind: "reset", token: resetToken };
    return null;
  });
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [tokenBusy, setTokenBusy] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Real data, fetched once on mount -- independently, so one teaser's
  // fetch failing does not blank the other two. Pre-login, so no token.
  useEffect(() => {
    fetchPublicFlows().then(setFlows).catch(() => setFlowsError(true));
    // 2026-09-22: the featured stock is no longer a hardcoded ticker --
    // the backend picks the real, currently-best-covered, highest-signal
    // large/mid/small-cap stock (never cap:micro) and caches it for the
    // day. See instrument.py's _pick_featured_stock for the selection rule.
    fetchFeaturedSignal().then(setSignal).catch(() => setSignalError(true));
    fetchPublicScorecard().then(setScorecard).catch(() => setScorecardError(true));
    fetchUniverseStats().then(setStats).catch(() => setStatsError(true));
  }, []);

  function showToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3200);
  }

  function scrollToGate(which: "invite" | "login") {
    setTab(which);
    document.getElementById("access")?.scrollIntoView({ behavior: "smooth" });
  }

  async function submitInviteRequest(e: React.FormEvent) {
    e.preventDefault();
    setInviteBusy(true);
    try {
      const res = await requestInvite(name, inviteEmail, useCase);
      showToast(
        res.owner_notified
          ? "Request received — we'll be in touch."
          : "Request received. (Notification email pending config — it's recorded either way.)",
      );
      setName("");
      setInviteEmail("");
    } catch (err) {
      showToast(err instanceof ApiError ? `Couldn't submit: ${err.message}` : "Couldn't submit — try again shortly.");
    } finally {
      setInviteBusy(false);
    }
  }

  async function submitTokenForm(e: React.FormEvent) {
    e.preventDefault();
    if (!pendingToken) return;
    if (newPassword.length < 8) {
      setTokenError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setTokenError("Passwords don't match.");
      return;
    }
    setTokenBusy(true);
    setTokenError(null);
    try {
      const res = pendingToken.kind === "invite"
        ? await acceptInvite(pendingToken.token, newPassword)
        : await confirmPasswordReset(pendingToken.token, newPassword);
      window.history.replaceState(null, "", window.location.pathname);
      onLoggedIn(res.token, res.role, res.user_id);
    } catch (err) {
      setTokenError(err instanceof ApiError ? err.message : "That link is invalid or has expired.");
    } finally {
      setTokenBusy(false);
    }
  }

  // A real invite/reset link lands here, not on the normal marketing page --
  // the SPA has no router (App.tsx's own manual-state pattern), so this is
  // a query-param branch at the top of the same component, checked once.
  if (pendingToken) {
    return (
      <div className="landing">
        <nav>
          <div className="l-wrap l-navin">
            <div className="app-brand"><span className="dot" />TICKERPICKER</div>
          </div>
        </nav>
        <section style={{ padding: "80px 0" }}>
          <div className="l-wrap" style={{ maxWidth: 420, margin: "0 auto" }}>
            <div className="l-panel">
              <h2 style={{ marginBottom: 4 }}>{pendingToken.kind === "invite" ? "Set your password" : "Choose a new password"}</h2>
              <p className="l-secp" style={{ margin: "0 0 18px" }}>
                {pendingToken.kind === "invite"
                  ? "One more step — set a password to activate your invite."
                  : "This link is single-use and expires an hour after it was requested."}
              </p>
              <form onSubmit={submitTokenForm}>
                <label className="l-fl">New password</label>
                <input className="l-fi" type="password" placeholder="••••••••" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                <label className="l-fl">Confirm password</label>
                <input className="l-fi" type="password" placeholder="••••••••" value={newPasswordConfirm} onChange={(e) => setNewPasswordConfirm(e.target.value)} required />
                {tokenError && <div className="error" style={{ marginTop: 10 }}>{tokenError}</div>}
                <button className="l-btn dark lg" type="submit" disabled={tokenBusy} style={{ width: "100%", justifyContent: "center", marginTop: 16 }}>
                  {tokenBusy ? "Working…" : pendingToken.kind === "invite" ? "Activate account →" : "Reset password →"}
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    );
  }

  async function submitLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError(null);
    try {
      const res = await login(loginEmail, loginPassword);
      onLoggedIn(res.token, res.role, res.user_id);
    } catch (err) {
      setLoginError(err instanceof ApiError ? err.message : "login failed");
    } finally {
      setLoginBusy(false);
    }
  }

  return (
    <div className="landing">
      <nav>
        <div className="l-wrap l-navin">
          <div className="app-brand"><span className="dot" />TICKERPICKER</div>
          <div className="l-navlinks">
            <a href="#disciplines">Approach</a>
            <a href="#flows">Market</a>
            <a href="#signal">Stocks</a>
            <a href="#honest">Evaluation</a>
            <a href="/market-myths">Market Myths</a>
            <a href="/working-paper">Working Paper</a>
            <a href="#access">Access</a>
          </div>
          <div className="l-navcta">
            <a className="l-btn" onClick={() => scrollToGate("login")}>Log in</a>
            <a className="l-btn dark" onClick={() => scrollToGate("invite")}>Request invite</a>
          </div>
        </div>
      </nav>

      <header className="l-hero">
        <div className="l-wrap">
          <span className="l-pill"><span className="l-dot" />Invite-only · NSE + BSE · daily-backtested signals</span>
          <h1 className="l-hero-t">Where <span className="l-grad">four disciplines</span><br />meet to pick one ticker.</h1>
          <p className="l-hero-sub">
            Fundamental, technical, quant, and deep research — converged into one top-down funnel over the entire
            NSE &amp; BSE universe. Every signal backtested against reality, every day.
          </p>
          <div className="l-hero-cta">
            <a className="l-btn acc lg" onClick={() => scrollToGate("invite")}>Request an invite →</a>
            <a className="l-btn lg" href="#flows">See it live</a>
          </div>
          <div className="l-invite-note">🔒 Closed beta — access by invitation. Already have one? Log in.</div>

          <div className="l-disc-strip">
            {/* h3, NOT h4 (2026-09-22 SEO pass) -- these were the first
               sub-heading under the page's one h1 with no h2 yet in
               between; jumping straight to h4 skipped two heading levels,
               which both screen readers and search engines penalize as a
               broken outline. */}
            <div className="l-dchip"><span className="l-dn">01</span><span className="l-di" style={{ color: "var(--ok)" }}>◈</span><h3>Fundamental</h3><p>Valuation, earnings, quality — point-in-time correct.</p></div>
            <div className="l-dchip"><span className="l-dn">02</span><span className="l-di" style={{ color: "var(--accent)" }}>◄►</span><h3>Technical</h3><p>150+ indicators, as-of-date, no repainting.</p></div>
            <div className="l-dchip"><span className="l-dn">03</span><span className="l-di" style={{ color: "var(--warn)" }}>◉</span><h3>Quant</h3><p>Segment models, promoted on live evidence.</p></div>
            <div className="l-dchip"><span className="l-dn">04</span><span className="l-di" style={{ color: "var(--bad)" }}>☰</span><h3>Deep research</h3><p>Flows, macro, provenance behind every call.</p></div>
          </div>

          {/* LIVE (2026-09-22) -- these were hardcoded static text ("3,150+
             Instruments", "4.3M bars") and were found, live, to have
             drifted materially wrong (real counts were ~37%/~12% lower).
             Fetched fresh via /api/market/universe-stats on every page
             load, same as the three other teasers -- never a frozen
             snapshot again. "—" is the honest loading/error state, not a
             fabricated placeholder number. */}
          <div className="l-statrow">
            <div className="l-stat">
              <div className="l-v">{statsError || !stats ? "—" : stats.stock_count.toLocaleString("en-IN")}</div>
              <div className="l-l">Instruments</div>
            </div>
            <div className="l-stat">
              <div className="l-v">
                {statsError || !stats || stats.history_start_year === null
                  ? "—"
                  : `${new Date().getFullYear() - stats.history_start_year}`}
                <span className="l-u">yr</span>
              </div>
              <div className="l-l">Exchange-native history</div>
            </div>
            <div className="l-stat">
              <div className="l-v">
                {statsError || !stats ? "—" : (stats.bars_count / 1_000_000).toFixed(1)}
                <span className="l-u">M</span>
              </div>
              <div className="l-l">Survivorship-correct bars</div>
            </div>
            <div className="l-stat"><div className="l-v">Daily</div><div className="l-l">Live-scored signals</div></div>
          </div>
        </div>
      </header>

      {/* TEASER 1: FII/DII flows. LIVE: GET /api/market/flows (public, no-auth) */}
      <section id="flows" style={{ padding: "64px 0" }}>
        <div className="l-wrap">
          <div className="l-rgrid" style={{ gridTemplateColumns: ".82fr 1.18fr" }}>
            <div>
              <div className="l-sec-label">Institutional flows</div>
              <h2 className="l-sech">Who's really moving<br />the market — daily</h2>
              <p className="l-secp">
                Foreign institutions selling into domestic buying — the read serious Indian investors check first.
                We show you exactly how much data backs it, not a chart smoothed over gaps.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 20 }}>
                <div className="l-lgrow"><span style={{ width: 11, height: 11, borderRadius: 3, background: "var(--ok)" }} /><span><b>DII</b> — domestic institutions</span></div>
                <div className="l-lgrow"><span style={{ width: 11, height: 11, borderRadius: 3, background: "var(--bad)" }} /><span><b>FII</b> — foreign institutional flows</span></div>
              </div>
            </div>
            <div className="l-browser">
              <BrowserBar url="app.tickerpicker.in/market · flows" />
              <div style={{ padding: "16px 16px 12px" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: ".88rem" }}>
                    FII / DII net flows <span style={{ fontFamily: "var(--font-mono)", fontSize: ".68rem", color: "var(--ink-faint)", fontWeight: 400 }}>₹ crore</span>
                  </div>
                </div>
                {flowsError ? (
                  <div className="note">Couldn't load live flow data right now — try again shortly.</div>
                ) : !flows ? (
                  <div className="note">Loading real flow data…</div>
                ) : !flows.available ? (
                  <div className="note">No FII/DII reading captured yet.</div>
                ) : (
                  <>
                    <div style={{ display: "flex", gap: 7 }}>
                      <div className="l-mtile"><div className="l-mt-l">FII · {flows.date}</div><div className="l-mt-v" style={{ color: (flows.fii_net ?? 0) >= 0 ? "var(--ok)" : "var(--bad)" }}>{fmtCrore(flows.fii_net)}</div></div>
                      <div className="l-mtile"><div className="l-mt-l">DII · {flows.date}</div><div className="l-mt-v" style={{ color: (flows.dii_net ?? 0) >= 0 ? "var(--ok)" : "var(--bad)" }}>{fmtCrore(flows.dii_net)}</div></div>
                    </div>
                    {/* Real recent-days trend (2026-09-22) -- the same
                       FlowsChart component the authenticated Market page
                       uses, fed a lightweight window (no VIX/z-score/
                       rolling-correlation, genuinely not computed for this
                       teaser). Was retired at build time (2026-09-06) when
                       fii_dii_flows had exactly 1 row; the historical
                       backfill landed 2,000+ rows shortly after but this
                       teaser was never revisited until now. */}
                    {flows.recent.length >= 2 && (
                      <div style={{ marginTop: 12 }}>
                        <FlowsChart points={flows.recent} />
                      </div>
                    )}
                    <div style={{ marginTop: 10, padding: "9px 11px", background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 8, fontSize: ".76rem", color: "var(--ink-soft)", display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <span>ⓘ</span>
                      <span>{flows.note}{flows.recent.length >= 2 ? ` — last ${flows.recent.length} captured sessions shown above.` : " — this teaser shows exactly what's captured, not a smoothed multi-day trend that doesn't exist yet."}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEASER 2: Stock signal. LIVE: GET /api/instrument/{id}/signal (public, no-auth) */}
      <section id="signal" className="l-band" style={{ padding: "64px 0" }}>
        <div className="l-wrap">
          <div className="l-rgrid" style={{ gridTemplateColumns: "1.18fr .82fr" }}>
            <div className="l-browser">
              <BrowserBar url={`app.tickerpicker.in/stocks/${signal?.display_ticker ?? "…"}`} />
              <div style={{ padding: 22 }}>
                {signalError ? (
                  <div className="note">Couldn't load a live signal right now — try again shortly.</div>
                ) : !signal ? (
                  <div className="note">Loading a real stock signal…</div>
                ) : (
                  <>
                    {(() => {
                      const r = signal.coverage_return;
                      const v = signal.coverage_volatility;
                      const dir = r.signal === null ? "flat" : r.signal > 0 ? "up" : r.signal < 0 ? "down" : "flat";
                      const dirColor = dir === "up" ? "var(--ok)" : dir === "down" ? "var(--bad)" : "var(--ink-faint)";
                      return (
                        <>
                          <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                            <div style={{ flex: "none", textAlign: "center", minWidth: 100 }}>
                              <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.9rem", color: dirColor }}>
                                {r.signal === null ? "—" : `${r.signal >= 0 ? "+" : ""}${(r.signal * 100).toFixed(2)}%`}
                              </div>
                              <div style={{ fontFamily: "var(--font-mono)", fontSize: ".7rem", color: "var(--ink-faint)" }} title={r.scale_note}>
                                {r.matched_horizon ? `${r.matched_horizon}-day forecast` : "no matched horizon"}
                              </div>
                            </div>
                            <div style={{ flex: 1, minWidth: 190 }}>
                              <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.45rem", letterSpacing: "-.02em" }}>{signal.display_ticker}</div>
                              <div style={{ fontFamily: "var(--font-mono)", fontSize: ".78rem", color: "var(--ink-faint)" }}>
                                {signal.company_name ?? signal.display_ticker} · {signal.sector_key ?? "sector unknown"}
                              </div>
                              <div style={{ display: "flex", gap: 8, marginTop: 11, flexWrap: "wrap" }}>
                                <span className="l-vchip" title={v.scale_note}><span className="l-vk">σ (rank)</span> {v.signal === null ? "—" : v.signal.toFixed(4)}</span>
                                {r.gaps.length > 0 && (
                                  <span className="l-vchip" title={r.gaps.map((g) => `${g.segment_key}: ${g.reason}`).join("\n")}>
                                    <span className="l-vk">gaps</span> {r.gaps.length}
                                  </span>
                                )}
                                {r.contributing_segments.length > 0 && (
                                  <span className="l-vchip"><span className="l-vk">segments</span> {r.contributing_segments.length}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div style={{ marginTop: 16, padding: "13px 15px", background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 10 }}>
                            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: ".68rem", letterSpacing: ".05em", textTransform: "uppercase", color: "var(--ink-faint)", marginBottom: 6 }}>Real provenance, not a black box</div>
                            <div style={{ fontSize: ".87rem", color: "var(--ink-soft)", lineHeight: 1.5 }}>
                              Blended from {r.contributing_segments.map((s) => s.segment_key).join(" + ") || "no contributing segments"}, h={r.matched_horizon ?? "—"}.
                              {r.gaps.length > 0 && ` ${r.gaps.length} other segment(s) have no champion model yet — shown, not hidden.`}{" "}
                              σ is rank-space (screening-only, never a literal %). <span style={{ color: "var(--accent)", fontWeight: 500 }}>Full provenance inside →</span>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
            <div>
              <div className="l-sec-label">The verdict</div>
              <h2 className="l-sech">Not a chart to<br />interpret. An answer.</h2>
              <p className="l-secp">
                Every name gets a real forecast — a segment-model blend, with its horizon, its rank-space
                confidence read, and every gap where a segment has no champion yet shown, not hidden. This is a
                real, currently-resolving stock's signal, not an illustration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TEASER 3: Claim vs reality. LIVE: GET /api/models/scorecard (public, no-auth, NEW aggregation) */}
      <section id="honest" style={{ padding: "64px 0" }}>
        <div className="l-wrap">
          <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 34px" }}>
            <div className="l-sec-label" style={{ textAlign: "center" }}>Radical honesty</div>
            <h2 className="l-sech" style={{ textAlign: "center" }}>Everyone shows you a backtest.<br />We show you the live number.</h2>
            <p className="l-secp" style={{ textAlign: "center", margin: "14px auto 0" }}>
              Every model here predicts daily and is scored against what actually happened. Rank-IC, not a
              percentage return — the real unit these models are built and judged on.
            </p>
          </div>
          {scorecardError ? (
            <div className="note" style={{ textAlign: "center" }}>Couldn't load the live scorecard right now — try again shortly.</div>
          ) : !scorecard ? (
            <div className="note" style={{ textAlign: "center" }}>Loading real claim-vs-reality data…</div>
          ) : scorecard.rows.length === 0 ? (
            // Honest-empty state (2026-09-22): a genuine, previously-real
            // gap on production (the /models/scorecard backend note
            // records exactly why -- no training_run rows to compare
            // against). Never render "Real numbers from 0 live models" as
            // if it were a normal reading; say plainly that the backtest
            // side of the comparison isn't populated yet.
            <div className="note" style={{ textAlign: "center" }}>
              No backtest-vs-live comparison on record yet for any model — an honest gap, not an error.
            </div>
          ) : (
            <>
              <div className="l-browser" style={{ maxWidth: 780, margin: "0 auto" }}>
                <BrowserBar url="app.tickerpicker.in/models · scorecard" />
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--rule-strong)" }}>
                      <th className="l-cth" style={{ textAlign: "left" }}>Model</th>
                      <th className="l-cth" style={{ textAlign: "right" }}>Backtest rank-IC</th>
                      <th className="l-cth" style={{ textAlign: "right" }}>Live rank-IC</th>
                      <th className="l-cth">n</th>
                      <th className="l-cth">Verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scorecard.rows.slice(0, 6).map((m) => {
                      const vc = m.verdict === "holds" ? "var(--ok)" : m.verdict === "mild decay" ? "var(--warn)" : "var(--bad)";
                      return (
                        <tr key={`${m.model_id}-${m.output_type}`} style={{ borderBottom: "1px solid var(--rule)" }}>
                          <td style={{ padding: "11px 14px", fontFamily: "var(--font-display)", fontWeight: 600 }}>
                            {m.target_ref} <span style={{ color: "var(--ink-faint)", fontWeight: 400 }}>· {m.output_type} · h{m.horizon}</span>
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "right", fontFamily: "var(--font-mono)", color: "var(--ink-faint)" }}>{fmtIC(m.backtest_mean)}</td>
                          <td style={{ padding: "11px 14px", textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 600 }}>{fmtIC(m.live_metric_value)}</td>
                          <td style={{ padding: "11px 14px", textAlign: "center", fontFamily: "var(--font-mono)", fontSize: ".72rem", color: "var(--ink-faint)" }}>
                            {m.live_sample_n.toLocaleString()}{m.live_small_n ? " ⚠" : ""}
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "center" }}>
                            <span style={{ fontFamily: "var(--font-mono)", fontSize: ".68rem", fontWeight: 600, color: vc, background: "var(--surface-2)", borderRadius: 4, padding: "2px 9px" }}>{m.verdict}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ textAlign: "center", fontFamily: "var(--font-mono)", fontSize: ".72rem", color: "var(--ink-faint)", marginTop: 12, maxWidth: 640, marginLeft: "auto", marginRight: "auto" }}>
                Real numbers from {scorecard.rows.length} live models, as of {scorecard.as_of}. {scorecard.caveat}
              </div>
            </>
          )}
        </div>
      </section>

      {/* FOUR DISCIPLINES (thesis) */}
      <section id="disciplines" className="l-band" style={{ padding: "66px 0" }}>
        <div className="l-wrap" style={{ textAlign: "center" }}>
          <div className="l-sec-label" style={{ textAlign: "center" }}>One platform, four lenses</div>
          <h2 className="l-sech" style={{ textAlign: "center", fontSize: "2.3rem" }}>Most tools do one thing.<br />TickerPicker does the convergence.</h2>
          <p className="l-secp" style={{ textAlign: "center", margin: "14px auto 0", maxWidth: 640 }}>
            Fundamentals tell you <em>what</em> a company is worth. Technicals tell you <em>when</em>. Quant tells
            you <em>how much to trust it</em>. Deep research tells you <em>why</em>. Alone, each is half a picture.
            Together, they pick the ticker.
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginTop: 30, flexWrap: "wrap" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: ".82rem", color: "var(--ink-faint)" }}>fundamental + technical + quant + research</div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, color: "var(--ink-faint)", fontSize: "1.15rem" }}>→</div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.08rem", color: "#fff", background: "var(--accent)", borderRadius: 100, padding: "8px 20px" }}>one backtested ticker</div>
          </div>
        </div>
      </section>

      {/* DEEP FEATURES -- named-only by design, never demonstrated here */}
      <section id="features" style={{ padding: "64px 0 20px" }}>
        <div className="l-wrap">
          <div style={{ textAlign: "center", maxWidth: 600, margin: "0 auto 40px" }}>
            <div className="l-sec-label" style={{ textAlign: "center" }}>And there's more inside</div>
            <h2 className="l-sech" style={{ textAlign: "center" }}>The depth you unlock<br />with an invite</h2>
            <p className="l-secp" style={{ textAlign: "center", margin: "14px auto 0" }}>What you've seen is the surface. Inside is the full research workbench.</p>
          </div>
          <div className="l-fgrid">
            <div className="l-fcard"><div className="l-fico">◱</div><h3>Top-down funnel</h3><p>Market → Index → Stock. Read the regime, drill to the strongest sector, land on the name.</p></div>
            <div className="l-fcard"><div className="l-fico">⊞</div><h3>Full-universe screener</h3><p>Stack fundamental, technical &amp; flow filters over 3,000+ names. Sort, save views, export.</p></div>
            <div className="l-fcard"><div className="l-fico">⚗</div><h3>Monte Carlo workbench</h3><p>Simulate forward paths, stress a position, see the whole distribution — not a point estimate.</p></div>
            <div className="l-fcard"><div className="l-fico">◉</div><h3>The model factory</h3><p>Build, backtest, and promote your own segment models. Feature Lab, ensembles, champion/challenger.</p></div>
            <div className="l-fcard"><div className="l-fico">◄►</div><h3>Instrument detail</h3><p>Interactive charts, any of 150+ indicators on demand, full provenance behind every call.</p></div>
            <div className="l-fcard"><div className="l-fico">☰</div><h3>Auditable data</h3><p>Exchange-native OHLCV, survivorship-correct history, point-in-time correctness. Staleness shown, not hidden.</p></div>
          </div>
        </div>
      </section>

      {/* INVITE / LOGIN GATE */}
      <section id="access" style={{ padding: "60px 0" }}>
        <div className="l-wrap">
          <div className="l-gate">
            <div className="l-gate-grid">
              <div>
                <span className="l-gate-pill">🔒 Closed beta · invitation required</span>
                <h2>Access is limited to<br />maintain signal integrity.</h2>
                <p>
                  When everyone uses the same signal, it stops being an edge. We onboard researchers, analysts, and
                  serious individual investors in small batches — and review every request personally.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 22 }}>
                  <div className="l-gate-check"><span style={{ color: "var(--ok)" }}>✓</span> No advertising, ever — you're the user, not the product</div>
                  <div className="l-gate-check"><span style={{ color: "var(--ok)" }}>✓</span> Full transparency on model decay</div>
                </div>
              </div>
              <div id="login" className="l-panel">
                <div className="l-tabs">
                  <button className={`l-tabb${tab === "invite" ? " on" : ""}`} onClick={() => setTab("invite")}>Request invite</button>
                  <button className={`l-tabb${tab === "login" ? " on" : ""}`} onClick={() => setTab("login")}>Log in</button>
                </div>
                {tab === "invite" ? (
                  <form onSubmit={submitInviteRequest}>
                    <label className="l-fl">Full name</label>
                    <input className="l-fi" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
                    <label className="l-fl">Email</label>
                    <input className="l-fi" type="email" placeholder="you@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
                    <label className="l-fl">How will you use TickerPicker?</label>
                    <select className="l-fi" value={useCase} onChange={(e) => setUseCase(e.target.value)}>
                      <option>Individual investor — research</option>
                      <option>Analyst / research desk</option>
                      <option>Fund / PMS</option>
                      <option>Academic / student</option>
                      <option>Other</option>
                    </select>
                    <button className="l-btn acc lg" type="submit" disabled={inviteBusy} style={{ width: "100%", justifyContent: "center", marginTop: 16 }}>
                      {inviteBusy ? "Submitting…" : "Submit application →"}
                    </button>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: ".68rem", color: "var(--ink-faint)", textAlign: "center", marginTop: 12 }}>
                      No spam. No card. Just a conversation. We review every request personally.
                    </div>
                  </form>
                ) : (
                  <form onSubmit={submitLogin}>
                    <label className="l-fl">Email</label>
                    <input className="l-fi" type="email" placeholder="you@example.com" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
                    <label className="l-fl">Password</label>
                    <input className="l-fi" type="password" placeholder="••••••••" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required />
                    {loginError && <div className="error" style={{ marginTop: 10 }}>{loginError}</div>}
                    <button className="l-btn dark lg" type="submit" disabled={loginBusy} style={{ width: "100%", justifyContent: "center", marginTop: 16 }}>
                      {loginBusy ? "Signing in…" : "Log in →"}
                    </button>
                    <div style={{ textAlign: "center", marginTop: 12 }}>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: ".72rem", color: "var(--ink-faint)" }}>
                        Password reset ships in Phase 10.
                      </span>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <div className="l-wrap" style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div className="app-brand"><span className="dot" />TICKERPICKER</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: ".72rem", color: "var(--ink-faint)" }}>Fundamental · technical · quant · deep research. One funnel.</div>
          <div style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: ".72rem", color: "var(--ink-faint)" }}>© 2026 · Invite-only beta · Not investment advice</div>
        </div>
      </footer>

      <div className={`l-toast${toast ? " show" : ""}`}>{toast}</div>
    </div>
  );
}
